/**
 * Reusable form field wrapper component
 * @module FormFields/FormFieldWrapper
 */

import React, { memo } from 'react';
import { ValidationErrorDisplay } from './ValidationErrorDisplay';

/**
 * Props for FormFieldWrapper component
 */
export interface FormFieldWrapperProps {
  /** Field label */
  label: React.ReactNode;
  /** Field ID */
  id: string;
  /** Field content */
  children: React.ReactNode;
  /** Error message to display (if any) */
  error?: string;
  /** Helper text to display when field is valid */
  helperText?: string;
  /** Show helper text (typically when field is valid) */
  showHelper?: boolean;
  /** Optional ref for error navigation */
  fieldRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Wrapper for form fields with consistent styling and error/helper text
 * Provides a standardized container for form inputs with automatic error and helper text display
 * 
 * @param props - Component props
 * @param props.label - Field label (can be string or ReactNode for complex labels)
 * @param props.id - Unique field ID
 * @param props.children - Field input element(s)
 * @param props.error - Error message to display below the field
 * @param props.helperText - Helper text to display when field is valid
 * @param props.showHelper - Whether to show helper text
 * @param props.fieldRef - Optional ref for programmatic focus/navigation to field
 * @returns FormFieldWrapper component
 */
export const FormFieldWrapper = memo<FormFieldWrapperProps>(({ 
  label, 
  id, 
  children, 
  error, 
  helperText, 
  showHelper,
  fieldRef,
}) => (
  <div
    ref={fieldRef}
    className="ds-form w-full"
    data-ds-theme="vessel"
    data-ds-variant="input"
    data-ds-version="1"
  >
    {typeof label === 'string' ? <label htmlFor={id}>{label}</label> : label}
    {children}
    {error && <ValidationErrorDisplay message={error} id={`${id}-error`} />}
    {showHelper && helperText && !error && (
      <div className="helper-text text-xs text-gray-500 mt-1!" id={`${id}-helper`}>
        {helperText}
      </div>
    )}
  </div>
));

FormFieldWrapper.displayName = 'FormFieldWrapper';
